import React, { useState, useEffect, useRef } from "react";
import { findNodeHandle, Platform, View } from "react-native";
import { Box } from "arkh";

import { UserLoggedInNavigatorScreenProps } from "../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

import {
  ActionCard,
  ActionCardIcon,
  ActionCardTitle,
} from "../../OS/Login/ActionCardTitle";
import { AuthenticatedUsersHorizontalList } from "../../OS/AuthenticatedUsers/AuthenticatedUsersHorizontalList";
import { useActionSheet } from "@expo/react-native-action-sheet";
import { useLogout } from "../../OS/Login/useLogin";
import { FormattedMessage, useIntl } from "react-intl";
import { graphql, useLazyLoadQuery } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { NoStorePageQuery } from "./__generated__/NoStorePageQuery.graphql";
import { useIsFocused } from "@react-navigation/native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { useInterval } from "widgets/useInterval";

export function NoStorePage(
  props: UserLoggedInNavigatorScreenProps<"NoStore">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerNoStorePage {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function usePollFetchKey(interval: number | null, fetchKey?: string) {
  const [key, setKey] = useState(0);
  useInterval(() => setKey(key + 1), interval);
  return (fetchKey ?? "") + key;
}

function InnerNoStorePage(
  props: UserLoggedInNavigatorScreenProps<"NoStore"> & { fetchKey: string }
) {
  const [hasStore, setHasStore] = useState(false);

  const pollKey = usePollFetchKey(hasStore ? null : 4000, props.fetchKey);

  const isFocused = useIsFocused();
  const data = useLazyLoadQuery<NoStorePageQuery>(
    graphql`
      query NoStorePageQuery($skip: Boolean!) {
        hasStore @skip(if: $skip)
        home {
          __typename
        }
      }
    `,
    {
      skip: hasStore || !isFocused,
    },
    {
      fetchPolicy: "store-and-network",
      fetchKey: pollKey,
    }
  );

  useEffect(() => {
    if (data?.hasStore && !hasStore) {
      setHasStore(true);
    } else if (data?.hasStore === false && hasStore) {
      setHasStore(false);
    }
  }, [data, hasStore]);

  const logout = useLogout();
  const { showActionSheetWithOptions } = useActionSheet();
  const ref = useRef<View | null>(null);
  const intl = useIntl();
  const insets = useSafeAreaInsets();
  const openLogoutSheet = () => {
    showActionSheetWithOptions(
      {
        options: [
          intl.formatMessage({
            defaultMessage: "Cancel",
            description: "Logout action sheet cancel option",
          }),
          intl.formatMessage({
            defaultMessage: "Log out",
            description: "Logout action sheet log out option",
          }),
        ],
        cancelButtonIndex: 0,
        destructiveButtonIndex: 1,
        anchor:
          ref.current && Platform.OS === "ios"
            ? (findNodeHandle(ref.current ?? null) ?? undefined)
            : undefined,
        containerStyle:
          Platform.OS === "android"
            ? {
                paddingBottom: insets.bottom,
              }
            : undefined,
      },
      async (index) => {
        if (index === 1) {
          logout();
        }
      }
    );
  };
  return (
    <Box style={{ flex: 1 }} insets={insets}>
      <AuthenticatedUsersHorizontalList
        backgroundColor="surface"
        onActiveUserPress={() => openLogoutSheet()}
        ref={ref}
      />
      <Box
        style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
        space="large"
      >
        <ActionCard>
          <ActionCardTitle>
            <FormattedMessage
              defaultMessage="You don't have access to any organizations"
              description="No store page title"
            />
          </ActionCardTitle>
          <ActionCardIcon name="truck" />
        </ActionCard>
      </Box>
    </Box>
  );
}
